import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { LoginDto } from "../types/Auth";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { parentServiceUrl } from "../utils/ServicesUrls";
import { toast } from "react-toastify";

const isDevelopment = process.env.NODE_ENV === "development";

const Login: React.FC = () => {
  const { login, setAuthToken } = useAuth();
  const navigate = useNavigate();
  const { control, handleSubmit } = useForm<LoginDto>({
    defaultValues: { username: "", password: "" },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isDevelopment) return;
    const handleMessage = async (event: MessageEvent) => {
      console.log("Origin :", event.origin);
      const targetOrigin = new URL(parentServiceUrl).origin;
      if (event.origin !== targetOrigin) return;
      if (!event.data || typeof event.data !== "object") return;
      if (event.data.type !== "AUTH_TOKEN") return;
      const { accessToken, rolePermissions } = event.data;
      console.log("Received token from parent:", accessToken);
      console.log("Received role permissions from parent:", rolePermissions);
      await sessionStorage.setItem("permissions", rolePermissions);
      await setAuthToken(accessToken);
      navigate("/audits");
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const onSubmit: SubmitHandler<LoginDto> = async (values) => {
    if (!isDevelopment) {
      toast.error("Manual login is disabled in production mode.");
      return;
    }

    setLoading(true);
    try {
      await login(values);
      navigate("/audits");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  if (!isDevelopment) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h6">Please wait...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      {isDevelopment && (
        <Card sx={{ width: { xs: "100%", sm: 400 }, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h5" align="center" gutterBottom>
              <img
                src="/logo.png"
                alt="App Logo"
                style={{ width: 250, height: 100 }}
              />
            </Typography>
            <Typography variant="h6" align="center" gutterBottom>
              Welcome! Please login...
            </Typography>

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Controller
                name="username"
                control={control}
                rules={{ required: "Username is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Username"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="password"
                control={control}
                rules={{ required: "Password is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Password"
                    fullWidth
                    margin="normal"
                    type={showPassword ? "text" : "password"}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password"
                            onClick={() => setShowPassword((s) => !s)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ mt: 3 }}
                disabled={loading}
              >
                {loading ? "Signing in..." : "Sign in"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Login;
